import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Divider,
  Tooltip,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import ChartPopup from './ChartPopup';

// Watchlist quotes come from /api/market-quotes, which prefers the Schwab
// streaming feed and falls back to REST — no account lookup needed here.
const WatchlistBox = () => {
  const [tickers, setTickers] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [input, setInput] = useState('');
  const timerRef = useRef(null);
  const [activeSym, setActiveSym] = useState(null);
  const [orderBy, setOrderBy] = useState('symbol'); // 'symbol' | 'last' | 'change' | 'change_percentage'
  const [order, setOrder] = useState('asc'); // 'asc' | 'desc'
  const [showBidAsk, setShowBidAsk] = useState(() => {
    const saved = localStorage.getItem('watchlist_showBidAsk');
    return saved === 'true';
  });
  const [chartSymbol, setChartSymbol] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [earningsDays, setEarningsDays] = useState({}); // symbol -> { date, days }

  const handleToggleBidAsk = () => {
    const newVal = !showBidAsk;
    setShowBidAsk(newVal);
    localStorage.setItem('watchlist_showBidAsk', newVal);
  };

  const handleChartClick = (symbol) => {
    setChartSymbol(symbol);
    setShowChart(true);
  };

  const handleCloseChart = () => {
    setShowChart(false);
    setChartSymbol(null);
  };

  // Column width state with localStorage persistence and simple drag-resize
  const MIN_COL_WIDTH = 60;
  const MAX_COL_WIDTH = 240;
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('watchlist_col_widths_v2') || '{}');
      return {
        symbol: Number(saved?.symbol) || 72,
        last: Number(saved?.last) || 55,
        change: Number(saved?.change) || 45,
        change_percentage: Number(saved?.change_percentage) || 60,
        action: Number(saved?.action) || 36,
      };
    } catch {
      return { symbol: 72, last: 55, change: 45, change_percentage: 60, action: 36 };
    }
  });
  const colResizeRef = useRef({ active: false, key: null, startX: 0, startWidth: 0 });

  const onColMouseMove = (e) => {
    const r = colResizeRef.current;
    if (!r.active) return;
    const dx = e.clientX - r.startX;
    const newW = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, r.startWidth + dx));
    setColWidths(prev => {
      const next = { ...prev, [r.key]: newW };
      try { localStorage.setItem('watchlist_col_widths_v2', JSON.stringify(next)); } catch {}
      window.dispatchEvent(new Event('watchlist-col-resize'));
      return next;
    });
  };

  const stopColResize = () => {
    const r = colResizeRef.current;
    if (!r.active) return;
    r.active = false;
    window.removeEventListener('mousemove', onColMouseMove);
    window.removeEventListener('mouseup', stopColResize);
    document.body.style.cursor = '';
  };

  const startColResize = (key) => (e) => {
    e.preventDefault();
    const th = e.currentTarget.parentElement;
    const rect = th?.getBoundingClientRect();
    const w = (rect?.width || colWidths[key]);
    colResizeRef.current = { active: true, key, startX: e.clientX, startWidth: w };
    window.addEventListener('mousemove', onColMouseMove);
    window.addEventListener('mouseup', stopColResize);
    document.body.style.cursor = 'col-resize';
  };

  useEffect(() => () => {
    window.removeEventListener('mousemove', onColMouseMove);
    window.removeEventListener('mouseup', stopColResize);
  }, []);

  // Load watchlist tickers from backend
  useEffect(() => {
    const loadTickers = async () => {
      try {
        const resp = await apiRequest('/api/watchlist');
        const symbols = resp.data?.symbols || [];
        setTickers(Array.isArray(symbols) ? symbols : []);
      } catch (e) {
        console.error('Watchlist: failed to load tickers', e);
      }
    };
    loadTickers();
  }, []);

  // Days until next earnings for each ticker (earnings_calendar covers ~60 days out)
  useEffect(() => {
    const loadEarnings = async () => {
      if (!tickers.length) { setEarningsDays({}); return; }
      try {
        const resp = await apiRequest(`/api/earnings?symbols=${tickers.map(encodeURIComponent).join(',')}`);
        const dates = resp.data?.earnings || {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const map = {};
        Object.entries(dates).forEach(([sym, dateStr]) => {
          const d = new Date(`${dateStr}T00:00:00`);
          const days = Math.round((d - today) / 86400000);
          map[sym.toUpperCase()] = { date: dateStr, days };
        });
        setEarningsDays(map);
      } catch (e) {
        console.error('Watchlist: failed to fetch earnings', e);
      }
    };
    loadEarnings();
  }, [tickers]);

  const addTicker = async () => {
    const sym = String(input).toUpperCase().trim();
    if (!sym) return;
    try {
      await apiRequest('/api/watchlist', { method: 'POST', body: JSON.stringify({ symbol: sym }) });
      if (!tickers.includes(sym)) setTickers(prev => [...prev, sym]);
    } catch (e) {
      console.error('Watchlist: failed to add symbol', e);
    }
    setInput('');
  };

  const removeTicker = async (sym) => {
    try {
      await apiRequest(`/api/watchlist/${encodeURIComponent(sym)}`, { method: 'DELETE' });
      setTickers(prev => prev.filter(s => s !== sym));
    } catch (e) {
      console.error('Watchlist: failed to remove symbol', e);
    }
  };

  const fetchQuotes = async (force = false) => {
    if (!force && document.hidden) return;
    if (!tickers.length) { setQuotes({}); return; }

    try {
      const resp = await apiRequest(`/api/market-quotes?symbols=${tickers.map(encodeURIComponent).join(',')}`);
      const quoteMap = resp.data?.quotes || {};
      const out = {};
      tickers.forEach(orig => {
        const q = quoteMap[String(orig).toUpperCase()] || {};
        out[orig] = {
          symbol: orig,
          last: q.last ?? 0,
          change: q.change ?? 0,
          change_percentage: q.changePct ?? 0,
          bid: q.bid ?? 0,
          ask: q.ask ?? 0,
        };
      });
      setQuotes(out);
    } catch (e) {
      console.error('Watchlist: failed to fetch quotes', e);
    }
  };

  // Refresh every 2 seconds — server reads from the streaming cache, so this
  // is an in-memory lookup, not a broker API call per tick.
  useEffect(() => {
    fetchQuotes(true);
    timerRef.current = setInterval(() => fetchQuotes(false), 2000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickers]);

  return (
    <>
      <Card sx={{ m: 0, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <CardContent sx={{ p: 1, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle1">Watchlist</Typography>
            <Box>
              <Tooltip title={showBidAsk ? "Show Change" : "Show Bid/Ask"}>
                <IconButton size="small" onClick={handleToggleBidAsk}>
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh now">
                <IconButton size="small" onClick={() => fetchQuotes(true)}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
          <TextField
            size="small"
            placeholder="Add ticker (e.g., TSLA)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addTicker(); }}
            fullWidth
          />
          <IconButton color="primary" onClick={addTicker} aria-label="add ticker">
            <AddIcon />
          </IconButton>
        </Box>
        <Divider />
        <TableContainer sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.25, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'symbol' ? order : false} sx={{ width: colWidths.symbol, position: 'relative' }}>
                  <TableSortLabel
                    active={orderBy === 'symbol'}
                    direction={orderBy === 'symbol' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'symbol' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('symbol');
                    }}
                  >
                    Symbol
                  </TableSortLabel>
                  <Box onMouseDown={startColResize('symbol')} sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'col-resize' }} />
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'last' ? order : false} sx={{ width: colWidths.last, position: 'relative' }}>
                  <TableSortLabel
                    active={orderBy === 'last'}
                    direction={orderBy === 'last' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'last' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('last');
                    }}
                  >
                    Price
                  </TableSortLabel>
                  <Box onMouseDown={startColResize('last')} sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'col-resize' }} />
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'change' ? order : false} sx={{ width: colWidths.change, position: 'relative' }}>
                  <TableSortLabel
                    active={orderBy === 'change'}
                    direction={orderBy === 'change' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'change' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('change');
                    }}
                  >
                    {showBidAsk ? 'Bid' : 'Chg'}
                  </TableSortLabel>
                  <Box onMouseDown={startColResize('change')} sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'col-resize' }} />
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === 'change_percentage' ? order : false} sx={{ width: colWidths.change_percentage, position: 'relative' }}>
                  <TableSortLabel
                    active={orderBy === 'change_percentage'}
                    direction={orderBy === 'change_percentage' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'change_percentage' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('change_percentage');
                    }}
                  >
                    {showBidAsk ? 'Ask' : 'Chg%'}
                  </TableSortLabel>
                  <Box onMouseDown={startColResize('change_percentage')} sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'col-resize' }} />
                </TableCell>
                <TableCell align="right" sx={{ width: colWidths.action, position: 'relative' }}>
                  <Box onMouseDown={startColResize('action')} sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'col-resize' }} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (tickers.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={5}>No tickers. Add some above.</TableCell>
                    </TableRow>
                  );
                }
                const rows = tickers.map(sym => {
                  const q = quotes[sym] || {};
                  return {
                    symbol: sym,
                    last: Number(q.last ?? 0),
                    change: Number(q.change ?? 0),
                    change_percentage: Number(q.change_percentage ?? 0),
                    bid: Number(q.bid ?? 0),
                    ask: Number(q.ask ?? 0),
                  };
                });
                const comparator = (a, b) => {
                  const dir = order === 'asc' ? 1 : -1;
                  if (orderBy === 'symbol') return a.symbol.localeCompare(b.symbol) * dir;
                  return (a[orderBy] - b[orderBy]) * dir;
                };
                const sorted = rows.sort(comparator);
                return sorted.map(row => {
                  const color = (row.change >= 0) ? 'success.main' : 'error.main';
                  const isActive = activeSym === row.symbol;
                  return (
                    <TableRow
                      hover
                      key={row.symbol}
                      onClick={() => handleChartClick(row.symbol)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontWeight: 600, width: colWidths.symbol }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {row.symbol}
                          {earningsDays[row.symbol.toUpperCase()] ? (() => {
                            const { days, date } = earningsDays[row.symbol.toUpperCase()];
                            return (
                              <Tooltip title={days === 0 ? `Earnings today (${date})` : `Earnings in ${days} day${days === 1 ? '' : 's'} (${date})`}>
                                <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem', bgcolor: days === 0 ? '#9c27b0' : days <= 7 ? '#f44336' : days <= 21 ? '#ff9800' : '#4caf50', color: '#fff' }}>
                                  {days === 0 ? 'E' : days}
                                </Avatar>
                              </Tooltip>
                            );
                          })() : null}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ width: colWidths.last }}>
                        ${row.last.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: showBidAsk ? 'text.primary' : color, width: colWidths.change }}>
                        {showBidAsk ? row.bid.toFixed(2) : row.change.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: showBidAsk ? 'text.primary' : color, width: colWidths.change_percentage }}>
                        {showBidAsk ? row.ask.toFixed(2) : `${row.change_percentage.toFixed(2)}%`}
                      </TableCell>
                      <TableCell align="right" sx={{ width: colWidths.action }}>
                        {isActive ? (
                          <IconButton aria-label="delete" onClick={(e) => { e.stopPropagation(); removeTicker(row.symbol); }} size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
    
    <ChartPopup 
      open={showChart}
      onClose={handleCloseChart}
      symbol={chartSymbol}
    />
  </>
  );
};

export default WatchlistBox;
